import { useState, useEffect } from "react";
import { useSip } from "@/contexts/SipContext";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserPlus, Phone, X } from "lucide-react";

import { API_URL } from "@/utils/api";
const API = API_URL;

// Format phone number for display
function formatPhone(number) {
  if (!number) return "";
  const clean = String(number).replace(/\D/g, "");
  if (clean.length === 10 && clean.startsWith("0")) {
    return `${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6)}`;
  }
  if (clean.length === 11 && clean.startsWith("64")) {
    return `+64 ${clean.slice(2, 4)} ${clean.slice(4, 7)} ${clean.slice(7)}`;
  }
  return number;
}

// Format duration for display
function formatDuration(seconds) {
  if (!seconds || seconds < 1) return "";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

export function AddContactAfterCallDialog() {
  const { endedCallInfo, dismissEndedCallPrompt } = useSip();
  const { token } = useAuth();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactData, setContactData] = useState({
    name: "",
    phone_number: "",
    company: "",
    email: "",
    notes: "",
    source: "personal"
  });
  
  // Open dialog when endedCallInfo is set
  useEffect(() => {
    if (endedCallInfo) {
      setContactData({
        name: endedCallInfo.name || "",
        phone_number: endedCallInfo.number || "",
        company: "",
        email: "",
        notes: endedCallInfo.duration 
          ? `Call on ${new Date().toLocaleDateString()} (${formatDuration(endedCallInfo.duration)})`
          : "",
        source: "personal"
      });
      setIsOpen(true);
    }
  }, [endedCallInfo]);
  
  const handleClose = () => {
    setIsOpen(false);
    dismissEndedCallPrompt();
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactData.name.trim() || !contactData.phone_number.trim()) {
      toast.error("Name and phone number are required");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const payload = {
        name: contactData.name.trim(),
        phone_number: contactData.phone_number.trim(),
        source: contactData.source || "personal"
      };
      
      if (contactData.company?.trim()) payload.company = contactData.company.trim();
      if (contactData.email?.trim()) payload.email = contactData.email.trim();
      if (contactData.notes?.trim()) payload.notes = contactData.notes.trim();
      
      await axios.post(`${API}/contacts`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const typeLabel = contactData.source === "company" ? "company" : "personal";
      toast.success(`${contactData.name} added to ${typeLabel} contacts`);
      handleClose();
    } catch (error) {
      console.error("Error adding contact:", error);
      toast.error(error.response?.data?.detail || "Failed to add contact");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!endedCallInfo) return null;
  
  const directionLabel = endedCallInfo.direction === "incoming" ? "from" : "to";
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md" data-testid="add-contact-after-call-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-cyan-500" />
            Add to Contacts?
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 pt-2">
            <Phone className="w-4 h-4" />
            <span>
              Call {directionLabel} <strong>{formatPhone(endedCallInfo.number)}</strong>
              {endedCallInfo.duration > 0 && (
                <span className="text-muted-foreground ml-1">
                  ({formatDuration(endedCallInfo.duration)})
                </span>
              )}
            </span>
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="contact-name">Name *</Label>
            <Input
              id="contact-name"
              data-testid="contact-name-input"
              placeholder="Enter contact name"
              value={contactData.name}
              onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
              autoFocus
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="contact-phone">Phone Number *</Label>
            <Input
              id="contact-phone"
              data-testid="contact-phone-input"
              value={contactData.phone_number}
              onChange={(e) => setContactData({ ...contactData, phone_number: e.target.value })}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="contact-company">Company</Label>
            <Input
              id="contact-company"
              data-testid="contact-company-input"
              placeholder="Company name (optional)"
              value={contactData.company}
              onChange={(e) => setContactData({ ...contactData, company: e.target.value })}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="contact-email">Email</Label>
            <Input
              id="contact-email"
              data-testid="contact-email-input"
              type="email"
              placeholder="Email address (optional)"
              value={contactData.email}
              onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="contact-notes">Notes</Label>
            <Textarea
              id="contact-notes"
              data-testid="contact-notes-input"
              placeholder="Add any notes about this contact"
              value={contactData.notes}
              onChange={(e) => setContactData({ ...contactData, notes: e.target.value })}
              rows={2}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="contact-type">Save As</Label>
            <Select
              value={contactData.source}
              onValueChange={(value) => setContactData({ ...contactData, source: value })}
            >
              <SelectTrigger id="contact-type" data-testid="contact-type-select">
                <SelectValue placeholder="Select contact type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">Personal Contact</SelectItem>
                <SelectItem value="company">Company Contact (shared)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              data-testid="dismiss-contact-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Not Now
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="save-contact-btn"
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {isSubmitting ? "Saving..." : "Save Contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
